// API Types based on OpenAPI documentation

// Profile Picture Types
export interface StartProfilePictureUploadDto {
  mimeType: string
  sizeBytes: number
  fileName: string
}

export interface StartProfilePictureUploadResponseDto {
  signedUrl: string
  uploadId: string
  expiresAt: string
}

export interface CompleteProfilePictureUploadDto {
  uploadId: string
}

export interface CompleteProfilePictureUploadResponseDto {
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
  profilePictureUrl: string
  processing: boolean
}

export interface GetProfilePictureResponseDto {
  profilePicture: {
    uuid: string
    updatedAt: string
    processing: boolean
    urls: {
      original: string
      small?: string
      medium?: string
      large?: string
    }
  }
}

export interface DeleteProfilePictureResponseDto {
  message: string
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    imageUrl: string | null
    updatedAt: string
  }
}

// Common Types
export interface ApiResponse<T> {
  data: T
}

export interface PaginatedResponse<T> {
  data: T[]
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
}

// Health Check Types
export interface HealthCheckResponse {
  status: string
  info?: Record<string, { status: string }>
  error?: Record<string, { status: string; message?: string }>
  details: Record<string, { status: string }>
}

// Reseller Types
export interface CreateResellerDto {
  name: string
  description?: string
  contactEmail: string
  contactPerson: string
  phoneNumber?: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipcode: string
  countryCode: string
  storagePricePerGbCents: number
  apiCallPriceCents: number
}

export interface ResellerResponseDto {
  id: string
  name: string
  description?: string
  status: 'active' | 'suspended'
  isBillingManaged: boolean
  contactEmail: string
  contactPerson: string
  phoneNumber?: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipcode: string
  countryCode: string
  storagePricePerGbCents: number
  apiCallPriceCents: number
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy?: string
}

export interface GetResellerResponseDto {
  data: ResellerResponseDto
}

export interface PatchResellerDto {
  name?: string
  description?: string
  contactEmail?: string
  contactPerson?: string
  phoneNumber?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipcode?: string
  countryCode?: string
  storagePricePerGbCents?: number
  apiCallPriceCents?: number
}

// Tenant Types
export interface ResellerDto {
  id: string
  name: string
}

export interface TenantUserDto {
  id: string
  email: string
  firstName: string
  lastName: string
  userRole: string
}

export interface CreateTenantPublicDto {
  name: string
  firstName: string
  lastName: string
  phoneNumber: string
  adminEmail: string
  adminPassword: string
}

export interface TenantDto {
  id: string
  name: string
  resellerId: string
  reseller: ResellerDto
  planId?: string
  status: 'pending_confirmation' | 'incomplete' | 'trialing' | 'active' | 'suspended' | 'cancelled'
  billingStatus?:
    | 'active'
    | 'past_due'
    | 'unpaid'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
  isBillingManaged: boolean
  billingCycleDay?: number
  nextBillingCycleDate?: string
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  adminEmail?: string
  tenantUsers: TenantUserDto[]
  plan: {
    id: string
    name: string
  }
  createdAt: string
  updatedAt: string
}

export interface UpdateTenantPlanDto {
  planId: string
}

export interface UpdateTenantDto {
  planId?: string
  name?: string
}

export interface PermanentDeleteTenantParams {
  id: string
  skipRetention?: boolean
}

export interface PermanentDeleteTenantResponseDto {
  tenantId: string
  tenantType: string
  tenantName: string
  previousStatus: string
  deletedAt: string
  cleanupSummary: {
    usersDeleted: number
    filesDeleted: number
    apiKeysDeleted: number
    quotaAllocationsCleared: boolean
    databaseSchemaDropped: boolean
    cognitoUsersDeleted: number
    billingAccountCleared: boolean
  }
}

// New DTOs for enhanced tenant management
export interface CreateQSEEnterpriseTenantDto {
  companyName: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  resellerId: string
  planId: string
}
export interface TenantManagementDto {
  id: string
  name: string
  tenantType: string
  resellerId: string
  reseller: ResellerDto
  planId?: string
  plan?: {
    id: string
    name: string
  }
  status: 'pending_confirmation' | 'incomplete' | 'trialing' | 'active' | 'suspended' | 'cancelled'
  firstName?: string
  lastName?: string
  phoneNumber?: string
  adminEmail?: string
  createdAt: string
  updatedAt: string
  nextBillingCycleDate?: string
}

export interface GetTenantsByResellerResponse {
  data: TenantDto[]
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
}

export interface GetUserTenantsResponse {
  data: TenantDto[]
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
}

// Public Endpoints Types
export interface ResendConfirmationCodeDto {
  email: string
}

export interface ResendConfirmationCodeResponseDto {
  sent: boolean
  message: string
}

export interface ConfirmUserDto {
  email: string
  confirmationCode: string
}

export interface ConfirmUserResponseDto {
  confirmed: boolean
  message: string
}

export interface VerifyEmailForDeletionDto {
  email: string
}

export interface VerifyEmailForDeletionResponseDto {
  exists: boolean
  userName?: string
  tenantName?: string
}

// Auth Types
export interface LoginDTO {
  email: string
  password: string
}

export interface UserDataResponse {
  id: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  imageUrl?: string | null
  userRole: string
  status: string
  emailConfirmed: boolean
  mfaStatus?: MfaStatus
  isActive: boolean
  lastLoginAt?: string | null
  createdAt: string
  updatedAt: string
  storageQuota?: number | null
  apiQuota?: number | null
  qpaSeeds?: number | null
  quotaSummary?: QuotaSummaryDto | null
  tenant?: {
    id: string
    name: string
    status?: string
  } | null
  type: string
  isPrimary?: boolean
}

export interface LoginResponse {
  accessToken: string
  idToken: string
  refreshToken: string
  user: UserDataResponse
}

export type MfaStatus = 'enabled' | 'disabled' | 'skipped'

export interface GetMeResponse {
  id: string
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  type: string
  userRole: 'SUPER_ADMIN' | 'TENANT_USER' | 'TENANT_ADMIN'
  /** True for the tenant's primary admin. Absent for super admins, who have no tenant. */
  isPrimary?: boolean
  isActive: boolean
  mfaStatus?: MfaStatus
  storageQuota?: number
  apiQuota?: number
  tenant?: {
    id: string
    name: string
    status:
      | 'pending_confirmation'
      | 'incomplete'
      | 'trialing'
      | 'active'
      | 'suspended'
      | 'cancelled'
    billingStatus?:
      | 'active'
      | 'past_due'
      | 'unpaid'
      | 'canceled'
      | 'incomplete'
      | 'incomplete_expired'
  } | null
  drive: {
    status: 'UNINITIALIZED' | 'ENCRYPTION_KEY_MISSING' | 'QUOTA_NOT_CONFIGURED' | 'READY'
    quotaSummary?: QuotaSummaryDto | null
  }
  billingInfo?: BillingInfoResponseDto
  lastLoginAt?: string | null
  createdAt: string
  updatedAt: string
  status: string
  qpaQuota?: {
    userId: string
    qpaSeeds: number
  } | null
}

export interface CreateUserDTO {
  firstName: string
  lastName: string
  email: string
  password: string
  phoneNumber?: string
  tenantId?: string
  resellerId?: string
}

export interface SetupUserDriveResponseDto {
  success: boolean
  message: string
}

export interface CreateUserResponse {
  id: string
  email: string
  phoneNumber?: string
  type: string
  isActive: boolean
  mfaStatus?: MfaStatus
  storageQuota?: number
  apiQuota?: number
}

export interface UpdateMfaStatusDTO {
  mfaStatus: MfaStatus
}

export interface UpdateMfaStatusResponse {
  id: string
  email: string
  mfaStatus: MfaStatus
  message: string
}

export interface UpdateMyProfileDto {
  firstName?: string
  lastName?: string
  phoneNumber?: string
}

export interface UpdateProfileResponseDto {
  userId: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  storageQuota: number
  apiQuota: number
  updatedAt: string
  message: string
}

export interface UpdateProfileDto {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  storageQuota?: number
  apiQuota?: number
  userRole?: string
  qpaQuota?: number
}

export interface UpdateProfileDTO {
  firstName?: string
  lastName?: string
  phoneNumber?: string
}

// User Management Types
export interface TenantUserResponseDto {
  id: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  imageUrl?: string
  userRole: 'SUPER_ADMIN' | 'TENANT_USER' | 'TENANT_ADMIN'
  status: 'invited' | 'active' | 'suspended'
  emailConfirmed: boolean
  mfaStatus?: MfaStatus
  isActive: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
  storageQuota?: number
  apiQuota?: number
  tenant?: {
    id: string
    name: string
    status:
      | 'pending_confirmation'
      | 'incomplete'
      | 'trialing'
      | 'active'
      | 'suspended'
      | 'cancelled'
    billingStatus?:
      | 'active'
      | 'past_due'
      | 'unpaid'
      | 'canceled'
      | 'incomplete'
      | 'incomplete_expired'
  } | null
  quotaSummary?: QuotaSummaryDto | null
}

export interface TenantUsersResponseDto {
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  data: TenantUserResponseDto[]
}

export interface SuspendUserResponseDto {
  userId: string
  email: string
  status: string
  suspendedAt: string
  message: string
}

export interface ActivateUserResponseDto {
  userId: string
  email: string
  status: string
  activatedAt: string
  message: string
}

export interface TransferPrimaryAdminDto {
  newAdminId: string
}

export interface TransferPrimaryAdminResponseDto {
  message: string
  newPrimaryId: string
}

// Invitation Types
export interface CreateInvitationDTO {
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  storageQuota: number
  apiQuota: number
  tenantId?: string
}

export interface CreateUserInvitationDTO {
  firstName: string
  lastName: string
  email: string
  storageQuota: number
  apiQuota: number
  tenantId?: string
}

export interface InvitationResponseDTO {
  userId: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  userRole: string
  storageQuota: number
  apiQuota: number
  status: string
  invitationExpiresAt: string
  createdAt: string
  invitationLink: string
}

export interface CreateUserInvitationResponse {
  userId: string
  email: string
  firstName: string
  lastName: string
  userRole: string
  storageQuota: number
  apiQuota: number
  status: string
  invitationExpiresAt: string
  createdAt: string
}

export interface ResendInvitationResponseDTO {
  invitationExpiresAt: string
  message: string
  invitationLink: string
}

export interface ResendUserInvitationResponse {
  invitationExpiresAt: string
  message: string
}

export interface GetInvitationInfoResponseDTO {
  invitationId: string
  invitationCode: string
  expiresAt: string
  isExpired: boolean
  isAccepted: boolean
  storageQuota: number
  apiQuota: number
  user: {
    userId: string
    email: string
    firstName: string
    lastName: string
    userRole: string
    status: string
  }
  createdAt: string
}

export interface AcceptInvitationDTO {
  password: string
}

export interface AcceptInvitationResponseDTO {
  success: boolean
  message: string
  user?: {
    id: string
    email: string
    firstName: string
    lastName: string
    userRole: string
  }
}

// MFA Types
export interface EnableMfaResponseDTO {
  mfaStatus: MfaStatus
}

export interface DisableMfaResponseDTO {
  mfaStatus: MfaStatus
}

export interface SkipMfaResponseDTO {
  mfaStatus: MfaStatus
}

export interface MfaStatusResponseDTO {
  mfaStatus: MfaStatus
}

// File Management Types
export interface StartSingleUploadUrlDto {
  name: string
  parentPath?: string
  mimeType: string
  mediaType: string
  sizeBytes: number
  parentId?: string
}

export interface FileListingItemDto {
  id: string
  name: string
  path: string
  parentId?: string
  isDir: boolean
  status: string
  sizeBytes?: number
  isFavorite: boolean
  trashedAt?: string
  effectivelyTrashedBy?: string
  mimeType?: string
  mediaType?: string
  createdAt: string
  updatedAt: string
  systemFolderType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

export interface StartSingleUploadResponseDto {
  signedUrl: string
  file: FileListingItemDto
}

export interface CompleteSingleUploadDto {
  fileId: string
  sizeBytes?: number
  etag?: string
}

export interface CompleteSingleUploadResponseDto {
  file: FileListingItemDto
}

export interface StartMultipartUploadUrlDto {
  name: string
  parentPath?: string
  mimeType: string
  mediaType: string
  sizeBytes: number
  parentId?: string
}

export interface StartMultipartUploadResponseDto {
  file: FileListingItemDto
}

export interface ResumeMultipartUploadUrlDto {
  fileId: string
  partsNumber: number
}

export interface MultipartPart {
  PartNumber: number
  url: string
}

export interface MultipartFileSignedUrlsResponseDto {
  parts: MultipartPart[]
}

export interface MultipartPartInfo {
  ETag: string
  PartNumber: number
}

export interface CompleteMultipartUploadDto {
  fileId: string
  sizeBytes?: number
  parts: MultipartPartInfo[]
}

export interface CompleteMultipartUploadResponseDto {
  file: FileListingItemDto
}

export interface CreateFolderDto {
  name: string
  parentPath?: string
  parentId?: string
}

export interface CreateFolderResponseDto {
  folder: FileListingItemDto
}

export interface LookupFileResponseDto {
  file?: FileListingItemDto
}

export enum MediaType {
  FOLDER = 'FOLDER',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  DOCUMENT = 'DOCUMENT',
  SPREADSHEET = 'SPREADSHEET',
  PRESENTATION = 'PRESENTATION',
  PDF = 'PDF',
  ARCHIVE = 'ARCHIVE',
  OTHER = 'OTHER',
}

export enum FileStatus {
  UPLOADING = 'UPLOADING',
  ENCRYPTING = 'ENCRYPTING',
  AVAILABLE = 'AVAILABLE',
  FAILED = 'FAILED',
}

export interface FilesSearchDto {
  limit?: number
  offset?: number
  parentPath?: string
  parentId?: string
  search?: string
  mediaType?: MediaType | undefined
  isDir?: boolean
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes' | 'trashedAt'
  sortOrder?: 'ASC' | 'DESC'
  createdFrom?: string
  createdTo?: string
  location?: string
  favoritesOnly?: boolean
  trashedOnly?: boolean
}

export interface FilesSearchResponse {
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
  data: FileListingItemDto[]
}

export interface UpdateFileDto {
  name?: string
}

export interface UpdateFileResponseDto {
  file: FileListingItemDto
}

export interface DownloadRequestResponseDto {
  status: 'PROCESSING' | 'AVAILABLE' | 'EXPIRED'
  url?: string
}

export interface DownloadInfoDto {
  status: 'PROCESSING' | 'AVAILABLE' | 'EXPIRED'
  url?: string
}

export interface BulkMoveFileDto {
  fileIds: string[]
  targetParentId?: string
  targetParentPath?: string
  force?: boolean
}

export interface BulkOperationDto {
  fileIds: string[]
}

export interface BulkOperationFailure {
  fileId: string
  error: string
}

export interface ConflictedFileDto {
  id: string
  name: string
}

export interface BulkOperationResponseDto {
  successCount: number
  failureCount: number
  conflictCount?: number
  successfulIds: string[]
  failures: BulkOperationFailure[]
  conflictedFiles?: ConflictedFileDto[]
}

export interface BulkMoveResponseDto {
  successCount: number
  failureCount: number
  successfulIds: string[]
  failures: BulkOperationFailure[]
  conflictCount: number
  conflictedFiles: ConflictedFileDto[]
}

export interface CopyFilesDto {
  fileIds: string[]
}

export interface RetryFileEncryptionResponseDto {
  file: FileListingItemDto
}

export interface AbortUploadResponseDto {
  message: string
}

// Plan Types
export type QpaServiceKey =
  | 'CBOM'
  | 'SBOM'
  | 'HBOM'
  | 'BUILDER'
  | 'PQC'
  | 'CSF'
  | 'NCSB'
  | 'PQCMM'
  | 'ACT'
  | 'QSCOPE'

export interface QpaServiceItemDto {
  serviceKey: QpaServiceKey
  enabled: boolean
}

export interface QpaServiceResponseDto {
  serviceKey: QpaServiceKey
}

export interface QpaServicesResponseDto {
  planId: string
  planName: string
  qpaSeedCount: number
  services: QpaServiceResponseDto[]
}

export interface CreatePlanDto {
  name: string
  description?: string
  audience: 'QSE Retail' | 'QSE Enterprise'
  stripePlanId?: string
  storageQuotaGb: number
  serviceUsageQuota: number
  qpaSeedCount?: number
  isActive?: boolean
  qpaServices?: QpaServiceItemDto[]
}

export interface PlanResponseDto {
  id: string
  name: string
  description?: string
  stripePlanId?: string
  storageQuotaGb: number
  storageQuotaBytes: number
  serviceUsageQuota: number
  qpaSeedCount?: number
  monthlyPriceCents: number
  audience: 'QSE Retail' | 'QSE Enterprise'
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy?: string
  billingProduct?: BillingProductDto
  isCurrentPlan?: boolean
  isSelectable?: boolean
  tenantCount?: number
  maxStorageQuotaGb: number
  maxApiQuota: number
  totalStorageUsageGb?: number
  totalServiceUsage?: number
  qpaServices?: QpaServiceResponseDto[]
}

export interface PlanTenantDTO {
  currentServiceUsage: number
  currentStorageUsageGb: number
  tenantId: string
  tenantName: string
  tenantStatus: 'incomplete' | 'trialing' | 'active' | 'suspended' | 'cancelled'
}

export interface PlanResponseWithProductNameDto {
  id: string
  name: string
  description?: string
  stripePlanId?: string
  storageQuotaGb: number
  serviceUsageQuota: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy?: string
  productName?: string
  billingProduct?: BillingProductDto
}

export interface BillingPriceDto {
  id: string
  amount: number
  currency: string
  interval: string
  intervalCount: number
  trialPeriodDays?: number
}

export interface BillingProductPriceDto {
  id: string
  amount: number
  currency: string
  interval: string
  intervalCount: number
  trialPeriodDays?: number
}

export interface BillingProductDto {
  id: string
  name: string
  description: string
  active: boolean
  defaultPrice: BillingPriceDto
  features: string[]
  createdAt: number
  updatedAt: number
  metadata: Record<string, any>
}

export interface PlanWithBillingDto {
  id: string
  name: string
  description?: string
  stripePlanId?: string
  storageQuotaGb: number
  storageQuotaBytes: number
  serviceUsageQuota: number
  audience: 'QSE Retail' | 'QSE Enterprise'
  isActive: boolean
  createdAt: string
  updatedAt: string
  billingProduct: BillingProductDto
  isCurrentPlan?: boolean
  isSelectable?: boolean
  tenantCount?: number
  maxStorageQuotaGb: number
  maxApiQuota: number
  productName?: string
  qpaSeedCount?: number
  qpaServices?: QpaServiceKey[]
}

export interface PlanUsageStatsDto {
  activeTenantCount: number
  trialTenantCount: number
  suspendedTenantCount: number
  totalTenantCount: number
  totalMrrCents: number
}

export interface PlanStripeIntegrationDto {
  hasStripeIntegration: boolean
  stripePlanDetails?: Record<string, any>
  lastSyncAt?: string
  syncStatus?: 'synced' | 'out_of_sync' | 'error' | 'never_synced'
}

export interface PlanResponseWithStatsDto {
  id: string
  name: string
  description?: string
  stripePlanId?: string
  storageQuotaGb: number
  serviceUsageQuota: number
  monthlyPriceCents: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy?: string
  usageStats: PlanUsageStatsDto
  stripeIntegration: PlanStripeIntegrationDto
}

export interface PatchPlanDto {
  name?: string
  description?: string
  stripePlanId?: string
  storageQuotaGb?: number
  serviceUsageQuota?: number
  monthlyPriceCents?: number
  isActive?: boolean
  qpaSeedCount?: number
  qpaServices?: QpaServiceItemDto[]
}

export interface PlanTenantResponseDto {
  tenantId: string
  tenantName: string
  tenantSlug: string
  tenantStatus: 'incomplete' | 'trialing' | 'active' | 'suspended' | 'cancelled'
  tenantBillingStatus?: 'active' | 'past_due' | 'cancelled'
  isBillingManaged: boolean
  tenantCreatedAt: string
  currentStorageUsageGb: number
  currentServiceUsage: number
  storageUtilizationPercent: number
  serviceUtilizationPercent: number
}

// Billing Types
export interface CreateCheckoutSessionDto {
  planId: string
}

export interface CreateCheckoutSessionResponseDto {
  sessionId: string
  url: string
  expiresAt: string
  metadata?: Record<string, any>
}

export interface CheckoutSessionResponseDto {
  id: string
  url: string
  status: string
  customer: string
  payment_status: string
  expires_at: number
  metadata: Record<string, string>
}

export interface CheckoutSessionStatusDto {
  id: string
  status: string
  payment_status: string
  customer: string
  subscription?: string
  amount_total: number
  currency: string
  metadata: Record<string, string>
}

export interface CheckoutSessionStatusResponseDto {
  id: string
  status: string
  payment_status?: string
  customer?: string
  subscription?: string | StripeSubscription
  amount_total?: number
  currency?: string
  metadata?: Record<string, any>
}

export interface WebhookResponseDto {
  received: boolean
  eventType: string
  message: string
}

// Portal Session Types
export interface PortalSessionResponseDto {
  url: string
}

export interface SubscriptionCancellationPortalResponseDto {
  url: string
}

export interface ResumeSubscriptionPortalResponseDto {
  url: string
}

export interface FullPortalResponseDto {
  url: string
}

export interface PlanUpdatePortalResponseDto {
  url: string
}

export interface PlanUpdateRequestDto {
  planId: string
}

// Stripe Types for Checkout Session Status
export interface StripeAutomaticTax {
  disabled_reason: string | null
  enabled: boolean
  liability: any | null
}

// Stripe Product Types
export interface StripeProductDto {
  id: string
  name: string
}

export interface StripeProductsResponseDto {
  products: StripeProductDto[]
  total: number
}

export interface StripeBillingMode {
  type: string
}

export interface StripeCancellationDetails {
  comment: string | null
  feedback: string | null
  reason: string | null
}

export interface StripeInvoiceSettings {
  account_tax_ids: any[] | null
  issuer: {
    type: string
  }
}

export interface StripeRecurring {
  aggregate_usage: string | null
  interval: string
  interval_count: number
  meter: any | null
  trial_period_days: number | null
  usage_type: string
}

export interface StripePrice {
  id: string
  object: string
  active: boolean
  billing_scheme: string
  created: number
  currency: string
  custom_unit_amount: any | null
  livemode: boolean
  lookup_key: string | null
  metadata: Record<string, any>
  nickname: string | null
  product: string
  recurring: StripeRecurring | null
  tax_behavior: string
  tiers_mode: string | null
  transform_quantity: any | null
  type: string
  unit_amount: number | null
  unit_amount_decimal: string | null
}

export interface StripePlan {
  id: string
  object: string
  active: boolean
  aggregate_usage: string | null
  amount: number
  amount_decimal: string
  billing_scheme: string
  created: number
  currency: string
  interval: string
  interval_count: number
  livemode: boolean
  metadata: Record<string, any>
  meter: any | null
  nickname: string | null
  product: string
  tiers_mode: string | null
  transform_usage: any | null
  trial_period_days: number | null
  usage_type: string
}

export interface StripeSubscriptionItem {
  id: string
  object: string
  billing_thresholds: any | null
  created: number
  current_period_end: number
  current_period_start: number
  discounts: any[]
  metadata: Record<string, any>
  plan: StripePlan
  price: StripePrice
  quantity: number
  subscription: string
  tax_rates: any[]
}

export interface StripeSubscriptionItems {
  object: string
  data: StripeSubscriptionItem[]
  has_more: boolean
  total_count: number
  url: string
}

export interface StripeTrialSettings {
  end_behavior: {
    missing_payment_method: string
  }
}

export interface StripePaymentSettings {
  payment_method_options: Record<string, any>
  payment_method_types: string[] | null
  save_default_payment_method: string
}

export interface StripeSubscription {
  id: string
  object: string
  application: string | null
  application_fee_percent: number | null
  automatic_tax: StripeAutomaticTax
  billing_cycle_anchor: number
  billing_cycle_anchor_config: any | null
  billing_mode: StripeBillingMode
  billing_thresholds: any | null
  cancel_at: number | null
  cancel_at_period_end: boolean
  canceled_at: number | null
  cancellation_details: StripeCancellationDetails
  collection_method: string
  created: number
  currency: string
  current_period_end: number
  current_period_start: number
  customer: string
  days_until_due: number | null
  default_payment_method: string | null
  default_source: string | null
  default_tax_rates: any[]
  description: string | null
  discount: any | null
  discounts: any[]
  ended_at: number | null
  invoice_settings: StripeInvoiceSettings
  items: StripeSubscriptionItems
  latest_invoice: string
  livemode: boolean
  metadata: Record<string, any>
  next_pending_invoice_item_invoice: string | null
  on_behalf_of: string | null
  pause_collection: any | null
  payment_settings: StripePaymentSettings
  pending_invoice_item_interval: any | null
  pending_setup_intent: string | null
  pending_update: any | null
  plan: StripePlan
  quantity: number
  schedule: string | null
  start_date: number
  status: string
  test_clock: string | null
  transfer_data: any | null
  trial_end: number | null
  trial_settings: StripeTrialSettings
  trial_start: number | null
}

// Plan with Billing Types
export interface BillingProductPriceDto {
  id: string
  amount: number
  currency: string
  interval: string
  intervalCount: number
  trialPeriodDays?: number
}

// Plan Tenant Types
export interface PlanTenantResponseDto {
  id: string
  name: string
  status: string
  billingStatus?: string
  createdAt: string
  currentUsage: {
    storageUsedGb: number
    apiCallsUsed: number
  }
  subscription: {
    id: string
    status: string
    currentPeriodStart: string
    currentPeriodEnd: string
  }
}

// MFA Response Types
export interface EnableMfaResponseDTO {
  message: string
  mfaStatus: MfaStatus
}

export interface DisableMfaResponseDTO {
  message: string
  mfaStatus: MfaStatus
}

export interface MfaStatusResponseDTO {
  mfaStatus: MfaStatus
}

// User Management Types
export interface SuspendUserResponseDto {
  userId: string
  email: string
  status: string
  suspendedAt: string
  message: string
}

export interface ActivateUserResponseDto {
  userId: string
  email: string
  status: string
  activatedAt: string
  message: string
}

export interface UpdateProfileDto {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  storageQuota?: number
  apiQuota?: number
}

// New File Management Types

// Security Types

export interface GenerateEntropyResponseDto {
  data: string
}

export interface GenerateNoiseResponseDto {
  data: string
}

export interface GenerateKeyRequestDto {
  keyType: 'RSA-4096' | 'AES-256'
}

export interface GenerateAESKeyResponseDto {
  key: string
  keySize: number
}

// Note: RSA-4096 keypair generation is async and returns empty object
// AES-256 returns immediately with GenerateAESKeyResponseDto

// Realtime Management Types
export interface RealtimeUserConnectionDTO {
  connectionId: string
}

export interface RealtimeConnectionResponseDto {
  connectionId: string
  status: 'connected' | 'disconnected'
  message: string
}

// Quota Types
export interface StorageUsageInfoDto {
  used: number
  quota: number
  utilizationPercent: number
  status: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  remaining: number
  formatted: {
    used: {
      GB: string
      MB: string
      KB: string
    }
    quota: {
      GB: string
      MB: string
      KB: string
    }
    remaining: {
      GB: string
      MB: string
      KB: string
    }
    pending?: {
      GB: string
      MB: string
      KB: string
    }
  }
  pendingBytes: number
}

export interface QuotaUsageInfoDto {
  used: number
  quota: number
  utilizationPercent: number
  status: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  remaining: number
  formatted: {
    used: {
      GB: string
      MB: string
      KB: string
    }
    quota: {
      GB: string
      MB: string
      KB: string
    }
    remaining: {
      GB: string
      MB: string
      KB: string
    }
  }
}

export interface ServiceUsageInfoDto {
  used: number
  quota: number
  utilizationPercent: number
  status: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  remaining: number
}

export interface QuotaSummaryDto {
  storage: StorageUsageInfoDto
  service: ServiceUsageInfoDto
  lastUpdated: string
}

export interface TenantQuotaSizeFormattedDto {
  GB: string
  MB: string
  KB: string
}

export interface TenantStorageQuotaFormattedDto {
  allocated: TenantQuotaSizeFormattedDto
  used: TenantQuotaSizeFormattedDto
  quota: TenantQuotaSizeFormattedDto
  allocationRemaining: TenantQuotaSizeFormattedDto
  usageRemaining: TenantQuotaSizeFormattedDto
  pending: TenantQuotaSizeFormattedDto
}

export interface TenantStorageQuotaSummaryDto {
  allocated: number
  allocationUtilizationPercent: number
  allocationRemaining: number
  allocationStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  used: number
  usageUtilizationPercent: number
  usageRemaining: number
  usageStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  quota: number
  pendingBytes: number
  formatted: TenantStorageQuotaFormattedDto
}

export interface TenantServiceQuotaSummaryDto {
  allocated: number
  allocationUtilizationPercent: number
  allocationRemaining: number
  allocationStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  used: number
  usageUtilizationPercent: number
  usageRemaining: number
  usageStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  quota: number
}

export interface TenantQpaQuotaSummaryDto {
  allocated: number
  allocationUtilizationPercent: number
  allocationRemaining: number
  allocationStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL' | 'EXCEEDED'
  used: number
  quota: number
}

export interface AssignQpaSeedsDto {
  userId: string
  seeds: number
  notes?: string
}

export interface RevokeQpaSeedsDto {
  userId: string
  seeds: number
  notes?: string
}

export interface QpaAssignmentResponseDto {
  id: string
  userId: string
  tenantId: string
  assignedBy: string
  seeds: number
  action: 'ASSIGN' | 'REVOKE'
  notes: string | null
  tenantQpaQuotaAtTime: number | null
  tenantAllocatedQpaAtTime: number | null
  createdAt: string
}

export interface TenantQuotaSummaryDto {
  storage: TenantStorageQuotaSummaryDto
  service: TenantServiceQuotaSummaryDto
  qpa?: TenantQpaQuotaSummaryDto
  lastUpdated: string
}

// Billing Info Types
export interface GracePeriodTargetPlanDto {
  id: string
  name: string
  storageQuotaBytes: number
  serviceUsageQuota: number
}

export interface QuotaAdjustmentGracePeriodStatusDto {
  hasActiveGracePeriod: boolean
  gracePeriodStart?: string
  gracePeriodEnd?: string
  targetPlanId?: string
  targetPlan?: GracePeriodTargetPlanDto
}

export interface BillingInfoResponseDto {
  quotaAdjustmentGracePeriodStatus?: QuotaAdjustmentGracePeriodStatusDto
}

// Security Types - Duplicate removed

// Realtime Types
export interface RealtimeUserConnectionDTO {
  connectionId: string
}

// Audit Trail Types
export interface AuditTrailEntry {
  id: string
  actor: string
  action: string
  resource: string
  description: string
  details: Record<string, any>
  ipAddress: string
  userAgent: string
  createdAt: string
  updatedAt: string
}

export interface AuditTrailResponse {
  data: AuditTrailEntry[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface AuditTrailFilters {
  actor?: string
  actors?: string[]
  action?: string
  actions?: string[]
  resource?: string
  startDate?: string
  endDate?: string
  timezone?: string
  page?: number
  limit?: number
}

// Reseller Types
export interface CreateResellerDTO {
  name: string
  description: string
  contactEmail: string
  contactPerson: string
  phoneNumber: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipcode: string
  countryCode: string
  storagePricePerGbCents: number
  apiCallPriceCents: number
  maxTenants?: number | null
}

export interface UpdateResellerDTO {
  name?: string
  description?: string
  contactEmail?: string
  contactPerson?: string
  phoneNumber?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipcode?: string
  countryCode?: string
  storagePricePerGbCents?: number
  apiCallPriceCents?: number
  maxTenants?: number | null
  status?: 'active' | 'inactive' | 'invited' | 'suspended'
  isBillingManaged?: boolean
}

export interface ResellerDto {
  id: string
  name: string
  description?: string
  status: 'active' | 'inactive' | 'invited' | 'suspended'
  isBillingManaged: boolean
  contactEmail: string
  contactPerson: string
  phoneNumber?: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipcode: string
  countryCode: string
  storagePricePerGbCents: number
  apiCallPriceCents: number
  maxTenants: number | null
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy?: string
  // Additional display fields
  storageLimit?: number
  storageUsed?: number
  usagePercent?: number
  lastActive?: string
  totalCustomers?: number
}

export interface CreateResellerResponse {
  id: string
  name: string
  contactEmail: string
  status: string
  createdAt: string
}

export interface GetResellersResponse {
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
  data: ResellerResponseDto[]
}

export interface GetResellerResponse {
  data: ResellerDto
}

export interface ResellerFilters {
  status?: string
  search?: string
  limit?: number
  offset?: number
}

// Tenant Filters
export interface TenantFilters {
  search?: string
  status?: 'active' | 'suspended' | 'pending_confirmation' | 'incomplete'
  limit?: number
  offset?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  resellerId?: string
  _sortTimestamp?: number // Internal property to force query refetch
}

// User Filters
export interface UserFilters {
  limit?: number
  offset?: number
  role?: 'TENANT_USER' | 'TENANT_ADMIN'
  status?: 'invited' | 'active' | 'suspended'
  search?: string
  emailConfirmed?: boolean
}

export interface GetUsersResponse {
  data: UserDataResponse[]
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
  totalCount: number
}

// Update My Profile DTO
export interface UpdateMyProfileDto {
  firstName?: string
  lastName?: string
  phoneNumber?: string
}

export interface PaymentFailureNotification {
  id: string
  tenantId: string
  subscriptionId: string
  failureReason: string
  failureDate: string
  gracePeriodStart: string
  gracePeriodEnd: string
  isActive: boolean
  retryAttempts: number
  lastRetryDate?: string
  nextRetryDate?: string
}

export interface GracePeriodStatus {
  isInGracePeriod: boolean
  gracePeriodStart?: string
  gracePeriodEnd?: string
  daysRemaining: number
  paymentFailureReason?: string
  retryAttempts: number
  nextRetryDate?: string
}

export interface BillingStatusUpdate {
  tenantId: string
  billingStatus: 'active' | 'past_due' | 'cancelled'
  subscriptionStatus: string
  gracePeriodStatus?: GracePeriodStatus
  lastPaymentDate?: string
  nextBillingDate?: string
  amountDue?: number
  currency?: string
}

// Folder Upload Types
export interface FolderNodeDto {
  id: string
  name: string
  children?: FolderNodeDto[]
}

export interface FolderUploadInitiateDto {
  id: string
  name: string
  parentId?: string
  parentPath?: string
  totalFiles: number
  totalSizeBytes: number
  folderStructure: FolderNodeDto[]
}

export interface FolderUploadInitiateResponseDto {
  id: string
  name: string
  path: string
  parentId?: string
  isDir: boolean
  status: string
  isFavorite: boolean
  effectivelyTrashedBy?: string
  createdAt: string
  updatedAt: string
  folderStructure?: FolderTreeItemDto[]
}

export interface FolderTreeItemDto {
  id: string
  name: string
  path: string
  parentId?: string
  isDir: boolean
  status: string
  isFavorite: boolean
  effectivelyTrashedBy?: string
  createdAt: string
  updatedAt: string
  folderStructure?: FolderTreeItemDto[]
}

export interface CreateFileRequestDto {
  id: string
  name: string
  sizeBytes: number
  mimeType: string
  mediaType:
    | 'FOLDER'
    | 'IMAGE'
    | 'VIDEO'
    | 'AUDIO'
    | 'DOCUMENT'
    | 'SPREADSHEET'
    | 'PRESENTATION'
    | 'PDF'
    | 'ARCHIVE'
    | 'OTHER'
  uploadType: 'SINGLE' | 'MULTIPART'
  partsCount?: number
}

export interface BatchFileUploadRequestDto {
  parentId: string
  files: CreateFileRequestDto[]
}

export interface FileUploadResponseDto {
  id: string
  name: string
  path: string
  parentId?: string
  isDir: boolean
  status: string
  isFavorite: boolean
  effectivelyTrashedBy?: string
  createdAt: string
  updatedAt: string
  uploadType: 'SINGLE' | 'MULTIPART'
  singleUrl?: string
  parts?: Array<{
    PartNumber: number
    url: string
  }>
}

export interface BatchFileUploadResponseDto {
  files: FileUploadResponseDto[]
}

export interface FolderUploadCancelResponseDto {
  success: boolean
  message: string
}

export interface FolderUploadCompleteResponseDto {
  success: boolean
  message: string
}

export interface UploadCompletionItemDto {
  id: string
  uploadType: 'SINGLE' | 'MULTIPART'
  parts?: Array<{
    ETag: string
    PartNumber: number
  }>
}

export interface BulkUploadCompleteRequestDto {
  completions: UploadCompletionItemDto[]
}
// API Key Types - Updated to match API spec
export interface CurrentKeyResponseDto {
  exists: boolean
  displayKey?: string
  createdAt?: string
}

export interface GenerateKeyResponseDto {
  key: string
  warning: string
}

// Tenant Management Types
export interface CreateTenantDto {
  name: string
  resellerId: string
  adminUser: {
    firstName: string
    lastName: string
    email: string
    phoneNumber?: string
  }
  address: {
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    zipcode: string
    countryCode: string
  }
}

export interface CreateTenantPublicWithAddressDto {
  name: string
  adminUser: {
    firstName: string
    lastName: string
    email: string
    phoneNumber?: string
    password: string
  }
  address: {
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    zipcode: string
    countryCode: string
  }
}

export interface CreateQSEEnterpriseTenantDto {
  name: string
  resellerId: string
  adminUser: {
    firstName: string
    lastName: string
    email: string
    phoneNumber?: string
  }
  address: {
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    zipcode: string
    countryCode: string
  }
  planId: string
}

export interface UpdateTenantPlanDto {
  planId: string
}

export interface GetUserTenantsResponse {
  data: TenantDto[]
  totalCount: number
  pageInfo: {
    hasPreviousPage: boolean
    hasNextPage: boolean
    startOffset: number
    endOffset: number
    totalPages: number
    count: number
  }
}

// Storage Reports Types
export interface GlobalAggregatesDto {
  totalActiveTenants: number
  totalPendingTenants: number
  totalActiveUsers: number
  totalStorageUsed: number
  lastSnapshotDate: string
}

export interface StorageUsageSummaryDto {
  used: number
  limit: number
  percentage: number
  allocated?: number
}

export interface ApiUsageSummaryDto {
  used: number
  limit: number
  percentage: number
  allocated?: number
}

export interface PaginationMetaDto {
  count: number
  endOffset: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  startOffset: number
  totalPages: number
}

export interface StorageReportResponseDto {
  aggregates: GlobalAggregatesDto
  data: TenantReportRowDto[]
  pageInfo: PaginationMetaDto
}

export interface LatestSnapshotReportResponseDto {
  date: string
}

// Tenant Analytics Types
export interface TenantContextDto {
  id: string
  name: string
  status: string
  planName: string
  type: string
  billingPeriodEnd?: string
  nextBillingCycleDate?: string
  storageUsageLimit: number
  registrationDate: string
  apiUsageLimit: number
}

export interface ChartDataPointDto {
  date: string
  value: number
  label?: string
  allocated?: number
}

export interface ChartDataDto {
  title: string
  type:
    | 'storage-usage'
    | 'api-usage'
    | 'user-growth'
    | 'bandwidth'
    | 'status-history'
    | 'plan-history'
  data: ChartDataPointDto[]
  metadata?: Record<string, unknown>
}

export interface MultiLineChartDataPointDto {
  date: string
  values: Record<string, number>
}

export interface MultiLineChartDataDto {
  title: string
  type:
    | 'storage-usage'
    | 'api-usage'
    | 'user-growth'
    | 'bandwidth'
    | 'status-history'
    | 'plan-history'
  data: MultiLineChartDataPointDto[]
  lines: Record<string, { label: string; color: string }>
  metadata?: Record<string, unknown>
}

export interface TimelineEventDto {
  date: string
  description: string
  previousValue: string
  newValue: string
  type?: string
}

export interface TimelineChartDataDto {
  title: string
  type:
    | 'storage-usage'
    | 'api-usage'
    | 'user-growth'
    | 'bandwidth'
    | 'status-history'
    | 'plan-history'
  events: TimelineEventDto[]
  metadata?: Record<string, unknown>
}

export interface ChartDataPointDto {
  date: string
  value: number
  label?: string
  allocated?: number
}

export interface MultiLineChartDataPointDto {
  date: string
  values: Record<string, number>
}

export interface StatusHistoryDto {
  date: string
  value: string
}

export interface PlanHistoryDto {
  date: string
  planName: string
  planStorageGB: number
  planApiLimit: number
}

export interface GlobalAggregatesDto {
  totalActiveTenants: number
  totalPendingTenants: number
  totalActiveUsers: number
  totalStorageUsed: number
  lastSnapshotDate: string
}

export interface StorageUsageSummaryDto {
  used: number
  limit: number
  percentage: number
  allocated?: number
}

export interface ApiUsageSummaryDto {
  used: number
  limit: number
  percentage: number
  allocated?: number
}

export interface TenantReportRowDto {
  id: string
  tenantName: string
  type: 'QSE Retail' | 'QSE Enterprise'
  status: 'Active' | 'Suspended' | 'pending_confirmation' | 'incomplete'
  planName: string
  billingPeriodEnd?: string
  nextBillingCycleDate?: string
  users: number
  storageUsage: StorageUsageSummaryDto
  apiUsage: ApiUsageSummaryDto
  registrationDate: string
  storageUsageLimit: number
  apiUsageLimit: number
}

export interface TenantAnalyticsResponseDto {
  tenant: TenantContextDto
  period: Record<string, unknown>
  storageUsage: ChartDataPointDto[]
  apiUsage: ChartDataPointDto[]
  userGrowth: MultiLineChartDataPointDto[]
  bandwidth: MultiLineChartDataPointDto[]
  statusHistory: StatusHistoryDto[]
  planHistory: PlanHistoryDto[]
  metadata?: Record<string, unknown>
}

// Bulk user invitation (QSE-994)

export type BulkInviteStatus = 'QUEUED' | 'PROCESSING' | 'SUCCEEDED' | 'PARTIAL' | 'FAILED'

/** A true row-level failure reported on a terminal request (not email-delivery issues). */
export interface BulkInviteRowError {
  row: number
  email: string
  code: string
  message: string
}

/** A row skipped before dispatch (never attempted), with its reason. */
export interface BulkInviteResultSkip {
  row: number
  email: string
  code: string
  message: string
  status: 'INVALID' | 'ALREADY_INVITED' | 'ALREADY_REGISTERED'
}

/**
 * Who ran the upload. History is tenant-wide, so a row may be someone else's
 * upload — `isSuperAdmin` is derived server-side and drives the fixed "Super Admin" label.
 */
export interface BulkInviteInitiator {
  id: string
  email: string | null
  firstName: string | null
  lastName: string | null
  role: string | null
  isSuperAdmin: boolean
}

/** `BulkInviteRequestResponseDTO` — returned by the status + list endpoints. */
export interface BulkInviteRequest {
  id: string
  status: BulkInviteStatus
  totalRows: number
  successCount: number
  failureCount: number
  /** Rows never attempted. `successCount + failureCount + skippedCount = totalRows`. */
  skippedCount: number
  emailsNotSent: number
  fileName: string | null
  /**
   * Always present from the read endpoints. Null only in a record seeded locally
   * from a 202 (which carries no initiator) before the first fetch lands.
   */
  initiator: BulkInviteInitiator | null
  requestedStorageBytes: number | null
  requestedApiQuota: number | null
  errors: BulkInviteRowError[] | null
  skipped: BulkInviteResultSkip[] | null
  createdAt: string | null
  completedAt: string | null
}

/* -------------------------------------------------------------------------- */
/*  Column mapping                                                            */
/* -------------------------------------------------------------------------- */

/** The canonical fields a column can supply. Mirrors the backend registry. */
export const BULK_INVITE_CANONICAL_FIELDS = [
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'role',
  'storage_quota',
  'api_quota',
] as const

export type BulkInviteCanonicalField = (typeof BULK_INVITE_CANONICAL_FIELDS)[number]

/** Required fields — a send is impossible until every one of these is mapped. */
export const BULK_INVITE_REQUIRED_FIELDS: BulkInviteCanonicalField[] = [
  'first_name',
  'last_name',
  'email',
]

/** How a column's field was decided. `fuzzy` is a guess worth flagging for review. */
export type BulkInviteMatchKind = 'exact' | 'fuzzy' | 'none'

/** One column of the uploaded file, with samples that make an ambiguous header readable. */
export interface BulkInviteColumn {
  index: number
  header: string
  samples: string[]
}

/** How one column resolved to a canonical field. `field: null` = unresolved/ignored. */
export interface BulkInviteResolvedColumn {
  index: number
  field: BulkInviteCanonicalField | null
  matchedBy: BulkInviteMatchKind
}

/**
 * `200` from `?dryRun=mapping` — cheap (no DB, no Cognito). Drives the Review
 * Column Mapping screen, which then edits client-side with no further calls.
 */
export interface BulkInviteMappingInspection {
  kind: 'mapping'
  columns: BulkInviteColumn[]
  mapping: BulkInviteResolvedColumn[]
  /** Required fields no column resolved to — flagged at the top of the screen. */
  unresolvedRequired: BulkInviteCanonicalField[]
  /** Fields two or more columns resolved to; the admin picks one. */
  ambiguous: BulkInviteCanonicalField[]
}

/**
 * The admin's mapping override, sent as JSON in the multipart `mapping` field.
 * Complete (not a patch) and keyed by column index, so the send is deterministic:
 * what the admin saw is exactly what runs.
 */
export interface BulkInviteMappingOverride {
  columns: { index: number; field: BulkInviteCanonicalField | null }[]
}

/* -------------------------------------------------------------------------- */
/*  Preview (dryRun=full)                                                     */
/* -------------------------------------------------------------------------- */

/** A row that will be invited. */
export interface BulkInviteValidRow {
  row: number
  email: string
}

/** A row that will not be invited, with the reason shown to the admin. */
export interface BulkInviteInvalidRow {
  row: number
  email?: string
  field: string
  code: string
  message: string
}

/** An email that already exists, so its row is skipped rather than invited. */
export interface BulkInviteAlreadyKnownRow {
  row: number
  email: string
  status: 'ALREADY_INVITED' | 'ALREADY_REGISTERED'
  code: string
  message: string
}

/**
 * `200` from `?dryRun=full` — the breakdown behind the Review step.
 * Note what is absent: a rejection. Row-level problems arrive as data, never a 400.
 */
export interface BulkInvitePreview {
  kind: 'preview'
  /** The mapping actually used — auto-resolved, or the override as submitted. */
  mapping: BulkInviteResolvedColumn[]
  /** Data rows after empty-row discard. The denominator everywhere. */
  totalRows: number
  validCount: number
  /** Blank rows discarded — informational only, never reaches persisted totals. */
  skippedEmptyCount: number
  validRows: BulkInviteValidRow[]
  invalidRows: BulkInviteInvalidRow[]
  alreadyInvited: BulkInviteAlreadyKnownRow[]
  /** Rows already invited under THIS request (re-upload only) — already done, not re-sent. */
  alreadyDoneCount: number
  /** @deprecated alias for validCount, kept by the backend for older callers. */
  rowCount: number
  totals: { storageBytes: number; apiCalls: number }
}

/** `202` accepted from the async send call. */
export interface BulkInviteQueued {
  kind: 'queued'
  requestId: string
  status: 'QUEUED'
  totalRows: number
}

export interface BulkInviteListFilters {
  limit?: number
  offset?: number
  status?: BulkInviteStatus
  /** Narrow the (now tenant-wide) history back to one initiator. */
  initiatedBy?: string
  /**
   * Cache-scoping only — NOT sent to the server (see `listBulkInviteRequests`,
   * which appends only limit/offset/status/initiatedBy). The backend scopes the
   * list by the `x-tenant-id` header, but that header lives outside the React
   * Query key, so a super admin switching tenants would otherwise be served the
   * previous tenant's cached history until a hard reload. Threading the active
   * tenant into the key gives each tenant its own cache entry.
   */
  tenantId?: string
}

export type BulkInviteRequestsResponse = PaginatedResponse<BulkInviteRequest>

/**
 * Outcome of a single file row returned by GET /users/invitations/bulk/:requestId/details.
 * The last three are rows that were never attempted (skipped), not failures.
 */
export type BulkInviteRowStatus =
  | 'INVITED'
  | 'ACCEPTED'
  | 'REVOKED'
  | 'EMAIL_NOT_SENT'
  | 'FAILED'
  | 'INVALID'
  | 'ALREADY_INVITED'
  | 'ALREADY_REGISTERED'
  | 'DELETED'

export interface BulkInviteRowDetail {
  row: number | null
  email: string
  firstName: string | null
  lastName: string | null
  status: BulkInviteRowStatus
  errorCode: string | null
  errorMessage: string | null
  invitedAt: string | null
}

export interface BulkInviteRowDetailsFilters {
  limit?: number
  offset?: number
  /** Only rows with one of these outcomes; pagination and count follow the filter. */
  status?: BulkInviteRowStatus[]
}

export type BulkInviteRowDetailsResponse = PaginatedResponse<BulkInviteRowDetail>
